import type {
  CSVRow,
  UnitRecord,
  UnitTransformer,
  UnitUploadContext,
} from '@/types/csv-upload'
import {
  PMS_PROVIDERS,
  validatePMSProvider,
  type PMSProvider,
} from '@super/shared-types'
import { appfolioUnitTransformer } from './transformers/appfolio-unit-transformer'
import {
  BaseCSVProcessor,
  type BaseCSVProcessingOptions,
  type BaseCSVResult,
} from './base-csv-processor'

export interface UnitCSVProcessingResult extends BaseCSVResult {
  units: UnitRecord[]
  sampleRows?: CSVRow[]
  headers?: string[]
}

function getUnitTransformer(provider: PMSProvider): UnitTransformer {
  switch (provider) {
    case PMS_PROVIDERS.APPFOLIO:
      return appfolioUnitTransformer
  }
  throw new Error(
    `No unit transformer implemented for PMS provider: ${provider}`,
  )
}

export class UnitCSVProcessor extends BaseCSVProcessor<
  UnitRecord,
  UnitCSVProcessingResult,
  UnitUploadContext
> {
  private transformer: UnitTransformer

  constructor(
    pmsProvider: string,
    companyId: string,
    options: BaseCSVProcessingOptions = {},
  ) {
    if (!companyId?.trim()) {
      throw new Error('Company ID is required')
    }

    const validatedProvider = validatePMSProvider(pmsProvider)
    const context: UnitUploadContext = {
      companyId,
      pmsProvider: validatedProvider,
    }

    super(context, options)
    this.transformer = getUnitTransformer(validatedProvider)
  }

  protected transformRow(row: CSVRow, _rowNumber: number): UnitRecord | null {
    return this.transformer.transformUnit(row, this.context)
  }

  protected validateRecord(
    record: UnitRecord,
    _rowNumber: number,
  ): { isValid: boolean; errors: string[]; warnings: string[] } {
    return this.transformer.validateUnit(record, this.context)
  }

  protected createResult(data: {
    success: boolean
    totalRows: number
    processedRows: number
    skipped: number
    records: UnitRecord[]
    errors: BaseCSVResult['errors']
    warnings: BaseCSVResult['warnings']
    sampleRows: CSVRow[]
    headers: string[]
    memoryUsage: { used: number; limit: number; percentage: number }
  }): UnitCSVProcessingResult {
    return {
      success: data.success,
      totalRows: data.totalRows,
      processedRows: data.processedRows,
      skipped: data.skipped,
      units: data.records,
      errors: data.errors,
      warnings: data.warnings,
      sampleRows: data.sampleRows,
      headers: data.headers,
      memoryUsage: data.memoryUsage,
    }
  }

  protected createErrorResult(
    errors: BaseCSVResult['errors'],
    memoryStatus: { used: number; limit: number; percentage: number },
  ): UnitCSVProcessingResult {
    return {
      success: false,
      totalRows: 0,
      processedRows: 0,
      skipped: 0,
      units: [],
      errors,
      warnings: [],
      sampleRows: [],
      headers: [],
      memoryUsage: memoryStatus,
    }
  }

  async processFile(file: File): Promise<UnitCSVProcessingResult> {
    return this.parseCSV(file)
  }
}
